// Sprint work-segment-collapse (PR-B, D1-D3/D6/D8) — render-time
// derivation of the WORK SEGMENT split: which render items between two
// agent messages may fold, and when. Supersedes `turn-collapse-final-
// answer`'s WORK/ANSWER/LOUD split (`splitTurnItems`/`TurnSplit`, retired
// here — see `git log --follow` for the prior shape): there is no single
// trailing "answer" any more, every agent message renders in stream order
// (D1); the collapse UNIT is the segment, not the turn.
//
// Nothing here is persisted. The backend never marks where one chunk of
// work ends and the next begins; both are derived from the message stream
// the timeline already holds:
//
//   • A work SEGMENT is a maximal run of non-`assistant_text` render items:
//     tool rows, action groups, subagent/workflow panels, non-loud system
//     pills (D1). It folds the moment the agent speaks after it, or the
//     turn closes (D2).
//   • LOUD pills and the compaction divider BREAK a segment, like an
//     `assistant_text` item does, and keep their chronological position
//     outside every panel (D6(a)/(b)) — they exist to be impossible to
//     miss (D013-EC-118, "loud, not silent").
//   • A pending permission / ask_user_question card makes its WHOLE
//     segment un-foldable, no wrapper element at all (D6(c)).
//
// A turn is CLOSED when a `result` / `exit` system event follows the
// founder's message in stream order (D2b). The LAST turn additionally
// requires the agent not to be working: the closing event of a previous
// turn must never collapse the live one.
//
// Two related sets live here, and they are deliberately DIFFERENT sizes —
// the first draft's comment claimed one shared set, which was false and
// which Gate 2 caught:
//
//   TURN_CLOSING_SUBTYPES      — result, exit. What proves a turn is OVER,
//                                and therefore what may be collapsed.
//   TURN_STALENESS_SUBTYPES    — the above plus `turn_boundary`. What
//                                proves a tool row can no longer be live
//                                (ConversationList's orphan-row pass).
//
// `turn_boundary` opens the NEXT turn, so it is proof enough that an
// earlier row is stale, but it is not proof that the turn it belongs to
// produced a result — a turn known only by its boundary keeps its work
// visible instead of collapsing on an assumption.

import type { Message } from '../../types';
import {
  asToolUse,
  LOUD_SYSTEM_SUBTYPES,
  SUBAGENT_TOOL_NAMES,
  WORKFLOW_TOOL_NAMES,
  isBackgroundSubagentLaunch,
  ackIsRealEnd,
  type BackgroundLaunchCtx,
} from '../messages';
import type { RenderItem } from './aggregate';
import type { TurnStatus } from './synthesize-summary';

/** System subtypes that close a turn (D2) — collapse eligibility. */
export const TURN_CLOSING_SUBTYPES = new Set(['result', 'exit']);

/**
 * Closing subtypes PLUS `turn_boundary` — the wider set that proves a
 * tool row can no longer be live. Consumed by ConversationList's
 * orphan-row pass (D013-EC-147), which pre-dates this module and used to
 * inline the same three strings.
 */
export const TURN_STALENESS_SUBTYPES = new Set([
  ...TURN_CLOSING_SUBTYPES,
  'turn_boundary',
]);

/** Synthetic id of the pre-conversation bucket (see ConversationList). */
export const LEAD_TURN_ID = '__lead__';

export interface TurnSection {
  turnId: string;
  items: RenderItem[];
}

export interface TurnMeta {
  turnId: string;
  closed: boolean;
  /** null while the turn is open. */
  status: TurnStatus | null;
  durationMs: number | null;
  /**
   * `TurnSummary` lookup key for this turn's verdict line (D2, sprint
   * turn-verdict-line). Defined EXACTLY as `ownClose?.message.id ?? null`
   * — never from `attributed`. `attributed = ownClose ?? closeAfter`
   * (below): on a mid-turn send, the section with no `result` of its own
   * borrows the next one's for duration purposes. If this field borrowed
   * too, both sections would resolve to the SAME `TurnSummary` and both
   * would paint the same verifications — two verdicts for one test run,
   * one of them on a turn that never ran it.
   */
  closingMessageId: string | null;
  /**
   * Count of actions the agent was blocked from taking this turn (D3's
   * `permission_denials.count`), 0 when absent/unknown. Sprint
   * turn-end-signals-line D6 — read from `ownClose` ONLY, never from
   * `attributed`, same rule and same reason as `closingMessageId`
   * immediately above: a section that borrows `attributed`'s duration
   * must not also borrow its denials, or two sections would paint the
   * same blocked-action count and one of them never triggered any.
   */
  blockedActionCount: number;
}

// Sprint compact-action-rows (D7) — `collapsible` and `defaultExpanded`
// were removed with the disclosure they served. Nothing reads them since
// the turn line stopped hiding work, and a computed-but-unread field is
// the dead-weight class a reviewer already caught once on this surface.
// `unresolvedPermissionMessageIds` and `developerMode` went with them:
// they existed only to answer "may this turn be collapsed?", a question
// the UI no longer asks.

export interface DeriveTurnMetaInput {
  turns: TurnSection[];
  /** Raw, unfiltered message stream (closing events are filtered out of
   *  renderItems in founder mode, so they can only be found here). */
  messages: Message[];
  agentWorking: boolean;
}

/** Every raw message an item covers (aggregations cover many). */
function itemMessages(item: RenderItem): Message[] {
  return item.kind === 'aggregation' ? item.items : [item.message];
}

function isSingleOfType(item: RenderItem, type: Message['type']): boolean {
  return item.kind === 'single' && item.message.type === type;
}

function isLoudPill(item: RenderItem): boolean {
  if (item.kind !== 'single' || item.message.type !== 'system') return false;
  const subtype = (item.message.content as { subtype?: unknown } | null)?.subtype;
  return typeof subtype === 'string' && LOUD_SYSTEM_SUBTYPES.has(subtype);
}

// Sprint compaction-divider-lock (2026-09-06, Santi chat) — the D12 hoist
// of the compaction divider was retired here first: `compact-action-rows`
// (#605) had deleted the collapsed work panel the hoist existed to rescue
// the divider FROM, so the divider rendered at its chronological position
// by default. Sprint work-segment-collapse restores a panel (the work
// segment) — the hoist does NOT return with it: instead the divider is a
// segment BREAKER (`isSegmentBreaker` below, D6(b)), which keeps it
// chronological AND keeps it outside every panel, the same outcome by a
// different, non-special-cased mechanism. Locked by `aggregate.test.ts` →
// "compaction divider keeps its chronological position".
// `'compaction_failed'` was never hoisted and is unchanged.

// Sprint work-segment-collapse (D1/D2/D3/D6/D8, PR-B) — the collapse unit
// moves from the TURN to the WORK SEGMENT: a maximal run of non-
// `assistant_text` render items between two agent messages (D1). Every
// `assistant_text` item, and the founder's own message, render OUTSIDE any
// (still the only thing `ConversationList` consumes, per B7 phasing) until
// the wiring phase retires them; nothing here reads or writes them.

/** D7 (B8) — id of the segment's first RAW message. */
function firstRawId(item: RenderItem): string {
  return item.kind === 'aggregation' ? (item.items[0]?.id ?? item.key) : item.message.id;
}

function itemStartMs(item: RenderItem): number | null {
  return item.kind === 'aggregation' ? item.startedAtMs : timestampMs(item.message);
}

/** D3 — "last settled activity": a group's own rule (`aggregate.ts`), or a
 * standalone `tool_use`'s paired result, or the item's own timestamp.
 *
 * Sprint subagent-launch-ack-liveness (D6) — 3rd param `bg` OPTIONAL: absent (legacy call
 * sites, 48 existing tests) keeps this function byte-identical to pre-sprint (AC10). */
function itemEndMs(
  item: RenderItem,
  resultByToolUseId: ReadonlyMap<string, Message>,
  bg?: BackgroundLaunchCtx,
): number | null {
  if (item.kind === 'aggregation') return item.lastActivityAtMs;
  if (item.message.type === 'tool_use') {
    const tu = asToolUse(item.message);
    // D6 — un lanzamiento de subagente en segundo plano NO se asienta con su recibo. Sin `bg`
    // (call sites legacy) esta rama no existe y el comportamiento es el de antes. El predicado
    // exige el NOMBRE de herramienta (B2): un `Bash` en segundo plano lleva el mismo flag y
    // nunca emite `task_notification`, así que caer aquí lo dejaría sin asentar para siempre.
    if (bg && isBackgroundSubagentLaunch(item.message)) {
      const ack = tu.tool_use_id ? resultByToolUseId.get(tu.tool_use_id) : undefined;
      if (ackIsRealEnd(ack)) return timestampMs(ack!); // EC-e: el fallo de arranque SÍ asienta
      const done = tu.tool_use_id ? bg.completionByToolUseId.get(tu.tool_use_id) : undefined;
      return done ? timestampMs(done) : null; // null = no asentado, NUNCA el recibo
    }
    const result = tu.tool_use_id ? resultByToolUseId.get(tu.tool_use_id) : undefined;
    if (result) return timestampMs(result);
  }
  return timestampMs(item.message);
}

/** D3 challenger item 7 — both shapes: group `errorCount`, or a standalone
 * failed `tool_use`, or a `type: 'error'` item. */
function itemFailureCount(item: RenderItem, resultByToolUseId: ReadonlyMap<string, Message>): number {
  if (item.kind === 'aggregation') return item.errorCount;
  const msg = item.message;
  if (msg.type === 'tool_use') {
    const tu = asToolUse(msg);
    const result = tu.tool_use_id ? resultByToolUseId.get(tu.tool_use_id) : undefined;
    const isError = !!result && (result.content as { is_error?: unknown } | null)?.is_error === true;
    return isError ? 1 : 0;
  }
  if (msg.type === 'error') return 1;
  return 0;
}

/** D3 size rule — a lone single row is "already one line"; a lone group,
 * subagent launch or workflow launch still folds. */
function isBareSize(items: RenderItem[]): boolean {
  if (items.length !== 1) return false;
  const only = items[0];
  if (!only || only.kind !== 'single') return false;
  if (only.message.type === 'tool_use') {
    const tu = asToolUse(only.message);
    if (SUBAGENT_TOOL_NAMES.has(tu.name) || WORKFLOW_TOOL_NAMES.has(tu.name)) return false;
  }
  return true;
}

function isSegmentBreaker(item: RenderItem): boolean {
  if (isSingleOfType(item, 'assistant_text')) return true;
  if (isLoudPill(item)) return true;
  // D6(b) — the compaction divider breaks a segment the same way, matching
  // `MessageBubble.tsx`'s own subtype check exactly.
  if (item.kind === 'single' && item.message.type === 'system') {
    const subtype = (item.message.content as { subtype?: unknown } | null)?.subtype;
    if (subtype === 'compaction') return true;
  }
  return false;
}

const EMPTY_UNRESOLVED_CARD_SET: ReadonlySet<string> = new Set();

export interface WorkSegment {
  /** D7 (B8) — id of the segment's first RAW message. Emitted as `data-segment-id`,
   *  used as the store key. NEVER `AggregationItem.key` (`agg-<id>`, aggregate.ts:228). */
  key: string;
  items: RenderItem[];
  /** D2 — derived, never the founder's expanded state (D8 / challenger item 5). */
  foldable: boolean;
  /** D3 size rule — false ⇒ the items render bare into the section: no header,
   *  no WorkSegment element, no `data-segment-id`. */
  fold: boolean;
  /** D6(d) — error/stopped tail: header present, panel open by default. */
  defaultExpanded: boolean;
  /** D3 — last settled activity − first item. null ⇒ header without duration. */
  durationMs: number | null;
  /** D3 — FIRST item's timestamp. NOT last-activity (that is the group rule). */
  startedAtMs: number | null;
  /** D3 challenger item 7 — group errorCount + standalone failed tool_use + `type:'error'`. */
  failureCount: number;
  /** Sprint subagent-launch-ack-liveness (D6) — un lanzamiento de subagente en segundo plano
   *  sin fin, con el padre trabajando y dentro de la cota de edad (B3). Cabecera
   *  `workSegment.headerLive` + Loader, sin duración. */
  live: boolean;
  /** D6 / C9 — hay un lanzamiento sin fin que NO puede llamarse vivo (padre parado, o pasada
   *  la cota de edad). Cabecera "sin confirmación de fin", sin Loader, sin duración.
   *  Mutuamente excluyente con `live`. */
  unconfirmed: boolean;
}

export type SectionPart =
  | { kind: 'item'; item: RenderItem }
  | { kind: 'segment'; segment: WorkSegment };

export interface WorkSegmentSplit {
  /** The founder's message. Never inside a segment. Replaces `TurnSplit.head`. */
  head: RenderItem | null;
  /** The render sequence AFTER the head, in stream order. */
  parts: SectionPart[];
}

function buildSegment(
  segItems: RenderItem[],
  meta: TurnMeta | undefined,
  resultByToolUseId: ReadonlyMap<string, Message>,
  unresolvedCardMessageIds: ReadonlySet<string>,
  speaksAfter: boolean,
  isTrailing: boolean,
  bg?: BackgroundLaunchCtx,
): WorkSegment {
  const firstItem = segItems[0] as RenderItem;
  const key = firstRawId(firstItem);
  // D6(c) — a pending permission / ask_user_question card with no terminal
  // outcome makes the WHOLE segment un-foldable, no wrapper element at all.
  const hasUnresolvedCard = segItems.some(
    (it) => it.kind === 'single' && unresolvedCardMessageIds.has(it.message.id),
  );
  const foldable = !hasUnresolvedCard && (speaksAfter || meta?.closed === true);
  const fold = foldable && !isBareSize(segItems);

  const startedAtMs = itemStartMs(firstItem);
  let lastActivityMs: number | null = null;
  let failureCount = 0;
  for (const it of segItems) {
    const end = itemEndMs(it, resultByToolUseId, bg);
    if (end !== null && (lastActivityMs === null || end > lastActivityMs)) lastActivityMs = end;
    failureCount += itemFailureCount(it, resultByToolUseId);
  }

  // D6 — un lanzamiento de subagente en segundo plano dentro del segmento, sin fin conocido.
  // Sólo `kind === 'single'`: nunca entra en una agregación (`aggregate.ts:164`).
  const unsettledBgLaunches = segItems.filter(
    (it) =>
      it.kind === 'single' &&
      isBackgroundSubagentLaunch(it.message) &&
      itemEndMs(it, resultByToolUseId, bg) === null,
  );
  // VIVO: hay uno sin fin, el padre trabaja, y NINGUNO de ellos ha pasado la cota de edad (B3).
  const live =
    bg !== undefined &&
    bg.agentWorking &&
    unsettledBgLaunches.length > 0 &&
    unsettledBgLaunches.some((it) => it.kind === 'single' && !bg.staleLaunchIds.has(it.message.id));
  // SIN CONFIRMAR (C9): hay uno sin fin pero no podemos llamarlo vivo. Sin este estado la
  // cabecera caería en `formatWorkedFor(null)` = "Trabajó" (`es.ts:1175`) y seguiría afirmando
  // trabajo TERMINADO sobre un hijo cuyo fin nadie confirmó — la queja literal de §1.
  const unconfirmed = bg !== undefined && !live && unsettledBgLaunches.length > 0;

  // Con un hijo sin asentar dentro, el segmento NO está asentado: no hay duración que decir.
  // El camino "header without duration" ya existe (`formatWorkedFor(null)`).
  const durationMs =
    live || unconfirmed
      ? null
      : startedAtMs !== null && lastActivityMs !== null && lastActivityMs >= startedAtMs
        ? lastActivityMs - startedAtMs
        : null;

  const defaultExpanded =
    fold && isTrailing && (meta?.status === 'error' || meta?.status === 'stopped');

  return {
    key,
    items: segItems,
    foldable,
    fold,
    defaultExpanded,
    durationMs,
    startedAtMs,
    failureCount,
    live,
    unconfirmed,
  };
}

/**
 * Split one turn section into head + a stream-ordered sequence of loose
 * items (prose, loud pills, the compaction divider) and work segments
 * (D1/D2/D3/D6). Deviation from the spec's 3-arg signature, flagged in the
 * plan: `unresolvedCardMessageIds` is a required 4th argument the module
 * cannot derive on its own (the terminal message that would prove a card
 * resolved is filtered out of `items` upstream) — optional here so the
 * 3-arg call the spec shows still type-checks.
 */
export function splitWorkSegments(
  items: RenderItem[],
  meta: TurnMeta | undefined,
  resultByToolUseId: ReadonlyMap<string, Message>,
  unresolvedCardMessageIds: ReadonlySet<string> = EMPTY_UNRESOLVED_CARD_SET,
  bg?: BackgroundLaunchCtx,
): WorkSegmentSplit {
  const first = items[0];
  const head = first && isSingleOfType(first, 'user') ? first : null;
  const rest = head ? items.slice(1) : items.slice(0);

  const parts: SectionPart[] = [];
  let current: RenderItem[] = [];

  const flush = (fromIndex: number, isTrailing: boolean) => {
    if (current.length === 0) return;
    const speaksAfter = rest.slice(fromIndex).some((it) => isSingleOfType(it, 'assistant_text'));
    parts.push({
      kind: 'segment',
      segment: buildSegment(current, meta, resultByToolUseId, unresolvedCardMessageIds, speaksAfter, isTrailing, bg),
    });
    current = [];
  };

  rest.forEach((item, idx) => {
    if (isSegmentBreaker(item)) {
      flush(idx, false);
      parts.push({ kind: 'item', item });
    } else {
      current.push(item);
    }
  });
  flush(rest.length, true);

  return { head, parts };
}

/** B10 — the anchor's signature, pure and directly testable. Group keys
 * keep the `compact-action-rows` D8 trigger; segment tokens carry the
 * DERIVED `foldable`, never `fold` and never the expanded state (challenger
 * item 5 — a signature that moved on a manual toggle would make the
 * anchor's only bottom action scroll past the rows just revealed). */
export function buildCollapseSignature(
  renderItems: readonly RenderItem[],
  segmentEntries: readonly { key: string; foldable: boolean }[],
): string {
  return [
    ...renderItems.filter((i) => i.kind === 'aggregation').map((i) => `${i.key}:${i.count}`),
    ...segmentEntries.map((s) => `seg-${s.key}:${s.foldable}`),
  ].join(',');
}

interface CloseEvent {
  index: number;
  message: Message;
}

function closingSubtype(m: Message): string | null {
  if (m.type !== 'system') return null;
  const subtype = (m.content as { subtype?: unknown } | null)?.subtype;
  return typeof subtype === 'string' ? subtype : null;
}

// Sprint turn-end-signals-line (D1, TES1 re-derived) — the CLI's own
// `is_error` partition of `terminal_reason`, transcribed verbatim from
// the installed CLI bundle at version 2.1.260
// (`grep -rhoa 'toe=[^;]\{0,400\}' /usr/lib/node_modules/@anthropic-ai/claude-code`,
// function `Xwt` classifies the partition). A CLI upgrade invalidates
// this set — re-extract before trusting it again. `aborted_streaming` /
// `aborted_tools` are deliberately NOT in this set: they carry the CLI's
// own "user aborted this" meaning (`uT`), but As Oasis's only turn
// cancellation path (the founder's Parar button → SIGTERM/SIGKILL) never
// emits a `result` event at all — the turn closes on `exit`, not
// `result` — so a branch keyed on either value here would be
// unreachable dead code in this architecture (§2 of the spec).
export const TERMINAL_REASON_ERROR_SET = new Set([
  'blocking_limit',
  'rapid_refill_breaker',
  'prompt_too_long',
  'image_error',
  'model_error',
  'api_error',
  'malformed_tool_use_exhausted',
  'budget_exhausted',
  'structured_output_retry_exhausted',
  'tool_deferred_unavailable',
  'turn_setup_failed',
]);

function statusOf(close: Message, stoppedInRange: boolean): TurnStatus {
  if (stoppedInRange) return 'stopped';
  const c = close.content as {
    subtype?: unknown;
    result_subtype?: unknown;
    status?: unknown;
    terminal_reason?: unknown;
  } | null;
  if (c?.subtype === 'result') {
    // Precedence, corrected at Gate 2 (B3, sprint turn-end-signals-line,
    // 2026-09-10): this function's return value (surfaced as `TurnMeta.status`
    // via `deriveTurnMeta`) is CANONICAL for the rendered turn status.
    // `synthesize-summary.ts`'s `summarize()` computes its own `TurnSummary.status`
    // from the SAME vocabulary but is NOT `terminal_reason`-corrected (no D1) and
    // is entered on a WIDER set of closes since D5 (`TURN_CLOSING_SUBTYPES`, not
    // just `subtype === 'result'`) — so the two diverge on a `result` close with
    // `result_subtype:'success'` + an error-class `terminal_reason` (this function
    // says 'error', `summarize` says 'success') and, systematically, on every
    // `exit` close (this function maps `finished`→'success', `summarize` always
    // returns 'stopped', unconditionally, since it has no `exit`-specific branch).
    // `TurnSummary.status` has NO reader today (`ConversationList.tsx` consumes
    // only `.verifications`) and MUST NOT gain one without being reconciled with
    // this function first — see the matching note at `synthesize-summary.ts`
    // `summarize()`. Fence log: results doc §7 F-13 (NOT spec §7 F-8, a different, uncrossed fence).
    const sub = typeof c.result_subtype === 'string' ? c.result_subtype : '';
    if (sub.includes('error')) return 'error';
    if (sub === 'success') {
      // D1 — the CLI's own classifier can call a `result_subtype:
      // 'success'` turn an error via `terminal_reason` (e.g. `api_error`
      // on an auth failure, observed in the B1 drill). Monotone and
      // fail-closed: only ever moves 'success' → 'error', never the
      // reverse, and an absent/unknown/future reason changes nothing —
      // generalises the same asymmetry the `exit` branch below already
      // applies ("an unknown / future status is treated as `stopped`,
      // never as success").
      const reason = typeof c.terminal_reason === 'string' ? c.terminal_reason : '';
      if (TERMINAL_REASON_ERROR_SET.has(reason)) return 'error';
      return 'success';
    }
    return 'stopped';
  }
  // `exit` carries an AgentStatus. Map the union EXPLICITLY: the first
  // draft returned `success` for everything that was not `'error'`, so a
  // turn closed by the legacy SIGTERM branch (`session-manager.ts`
  // `finalStatus = 'interrupted'`) rendered a calm "Worked for Xs" and
  // auto-collapsed its work — the exact inversion of D3 case 2. Caught by
  // Gate 2. `stoppedInRange` does not cover it: no backend path appends a
  // `stopped_by_user` system message today, so that branch is dead in
  // production. An unknown / future status is treated as `stopped`, never
  // as success — the safe direction is "show the founder the work".
  const status = typeof c?.status === 'string' ? c.status : '';
  if (status === 'finished') return 'success';
  if (status === 'error') return 'error';
  return 'stopped';
}

function timestampMs(m: Message | undefined): number | null {
  if (!m) return null;
  const parsed = Date.parse(m.timestamp);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Per-turn closure, status, duration and collapse eligibility (D2/D3/D5).
 */
export function deriveTurnMeta({
  turns,
  messages,
  agentWorking,
}: DeriveTurnMetaInput): Map<string, TurnMeta> {
  const indexById = new Map<string, number>();
  messages.forEach((m, i) => indexById.set(m.id, i));

  const closes: CloseEvent[] = [];
  const stoppedIndices: number[] = [];
  messages.forEach((m, i) => {
    const subtype = closingSubtype(m);
    if (subtype && TURN_CLOSING_SUBTYPES.has(subtype)) closes.push({ index: i, message: m });
    if (subtype === 'stopped_by_user') stoppedIndices.push(i);
  });

  // Turn boundaries in raw-stream coordinates, so a close event can be
  // attributed to the section it actually lands in (D5 + D6: a mid-turn
  // send splits one CLI turn across two sections; only the section holding
  // the `result` owns its duration).
  const userIndexOf = (turnId: string): number =>
    turnId === LEAD_TURN_ID ? -1 : indexById.get(turnId) ?? -1;

  const out = new Map<string, TurnMeta>();

  turns.forEach((turn, ti) => {
    // `ownClose` is not in scope here — this literal covers the
    // not-yet-closed / not-a-real-turn paths. `:277`'s case can coexist
    // with an ownClose when the last turn is still working, but
    // `closed: false` already suppresses TurnCollapse's verdict line, so
    // a real id here would never be read; null is the honest value.
    const notCollapsible: TurnMeta = {
      turnId: turn.turnId,
      closed: false,
      status: null,
      durationMs: null,
      closingMessageId: null,
      blockedActionCount: 0,
    };

    if (turn.turnId === LEAD_TURN_ID) {
      out.set(turn.turnId, notCollapsible);
      return;
    }

    const userIdx = userIndexOf(turn.turnId);
    if (userIdx < 0) {
      out.set(turn.turnId, notCollapsible);
      return;
    }

    const nextUserIdx = (() => {
      for (let j = ti + 1; j < turns.length; j += 1) {
        const section = turns[j];
        if (!section) continue;
        const idx = userIndexOf(section.turnId);
        if (idx >= 0) return idx;
      }
      return Number.POSITIVE_INFINITY;
    })();

    const closeAfter = closes.find((c) => c.index > userIdx) ?? null;
    const isLastTurn = ti === turns.length - 1;
    const closed = closeAfter !== null && !(isLastTurn && agentWorking);

    if (!closed) {
      out.set(turn.turnId, notCollapsible);
      return;
    }

    const ownClose =
      closes.find((c) => c.index > userIdx && c.index < nextUserIdx) ?? null;
    const attributed = ownClose ?? closeAfter;
    const upperBound = ownClose ? ownClose.index : nextUserIdx;
    const stoppedInRange = stoppedIndices.some(
      (i) => i > userIdx && i <= upperBound,
    );
    const status = statusOf(attributed.message, stoppedInRange);

    const durationMs = (() => {
      const raw = (ownClose?.message.content as { duration_ms?: unknown } | null)
        ?.duration_ms;
      if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) return raw;
      // D5 fallback — the section has no `result` of its own (mid-turn send).
      const lastMessage = turn.items
        .flatMap(itemMessages)
        .reduce<Message | undefined>((latest, m) => {
          const idx = indexById.get(m.id) ?? -1;
          const latestIdx = latest ? indexById.get(latest.id) ?? -1 : -1;
          return idx > latestIdx ? m : latest;
        }, undefined);
      const start = timestampMs(messages[userIdx]);
      const end = timestampMs(lastMessage);
      if (start === null || end === null || end < start) return null;
      return end - start;
    })();

    // D6 — read from `ownClose` ONLY, same rule as `closingMessageId`
    // above: `attributed` (which may be a borrowed `closeAfter`) must
    // never supply the count, or a borrowing section would paint
    // denials that happened in a different section's turn.
    const blockedActionCount = (() => {
      const raw = (
        ownClose?.message.content as {
          permission_denials?: { count?: unknown } | null;
        } | null
      )?.permission_denials?.count;
      return typeof raw === 'number' && Number.isFinite(raw) && raw >= 0 ? raw : 0;
    })();

    out.set(turn.turnId, {
      turnId: turn.turnId,
      closed: true,
      status,
      durationMs,
      closingMessageId: ownClose?.message.id ?? null,
      blockedActionCount,
    });
  });

  return out;
}
